// Temporary debug version - replace your AuthContext.jsx with this
import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Hardcoded configuration to avoid import issues
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5076/api'; // Update this to your actual backend URL
const API_ENDPOINTS = {
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REFRESH: '/auth/refresh'
};

// Storage keys
const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data'
};

// Initial state
const initialState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Reducer
const authReducer = (state, action) => {
  console.log('AuthReducer:', action.type, action.payload);
  
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return { ...state, isLoading: true, error: null };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return { ...initialState, isLoading: false };
    
    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload.isLoading };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Storage helpers
const getStoredToken = () => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    console.log('getStoredToken:', token ? 'exists' : 'null');
    return token;
  } catch (error) {
    console.error('Error getting stored token:', error);
    return null;
  }
};

const getStoredRefreshToken = () => {
  try {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    console.log('getStoredRefreshToken:', refreshToken ? 'exists' : 'null');
    return refreshToken;
  } catch (error) {
    console.error('Error getting stored refresh token:', error);
    return null;
  }
};

const getStoredUser = () => {
  try {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    const user = userData ? JSON.parse(userData) : null;
    console.log('getStoredUser:', user);
    return user;
  } catch (error) {
    console.error('Error getting stored user:', error);
    return null;
  }
};

const setStoredData = (token, refreshToken, user) => {
  try {
    console.log('setStoredData:', { token: !!token, refreshToken: !!refreshToken, user: !!user });
    if (token) localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    if (refreshToken) localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    if (user) localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
  } catch (error) {
    console.error('Error storing data:', error);
  }
};

const removeStoredData = () => {
  try {
    console.log('removeStoredData: Clearing localStorage');
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  } catch (error) {
    console.error('Error removing stored data:', error);
  }
};

// Simple token validation
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const decoded = JSON.parse(jsonPayload);
    const currentTime = Date.now() / 1000;
    const isExpired = decoded.exp < currentTime;
    console.log('isTokenExpired:', { currentTime, exp: decoded.exp, isExpired });
    return isExpired;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  console.log('AuthProvider state:', state);

  // Initialize auth state from storage
  useEffect(() => {
    const initializeAuth = () => {
      console.log('=== AuthContext: Starting initialization ===');
      
      const storedToken = getStoredToken();
      const storedRefreshToken = getStoredRefreshToken();
      const storedUser = getStoredUser();

      console.log('Stored data check:', {
        hasToken: !!storedToken,
        hasRefreshToken: !!storedRefreshToken,
        hasUser: !!storedUser,
        tokenExpired: storedToken ? isTokenExpired(storedToken) : 'no token'
      });

      if (storedToken && storedUser && !isTokenExpired(storedToken)) {
        console.log('=== Valid session found, setting authenticated ===');
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: storedUser,
            token: storedToken,
            refreshToken: storedRefreshToken
          }
        });
      } else {
        console.log('=== No valid session found, clearing data ===');
        removeStoredData();
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: { isLoading: false } });
      }
    };

    // Small delay to ensure everything is ready
    const timer = setTimeout(() => {
      initializeAuth();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Login function
  const login = async (credentials) => {
    console.log('=== Starting login process ===');
    console.log('Credentials:', { email: credentials.email });
    
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const url = `${API_BASE_URL}${API_ENDPOINTS.LOGIN}`;
      console.log('Login URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      console.log('Login response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log('Login error response:', errorData);
        throw new Error(errorData.message || 'Credențiale invalide');
      }

      const data = await response.json();
      console.log('=== Login response data ===:', data);

      const { token, refreshToken, user } = data;

      if (!token || !user) {
        console.error('Invalid response structure:', { hasToken: !!token, hasUser: !!user });
        throw new Error('Răspuns invalid de la server');
      }

      // Store data
      setStoredData(token, refreshToken, user);

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, token, refreshToken }
      });

      console.log('=== Login successful ===');
      return { success: true, user };
    } catch (error) {
      console.error('=== Login error ===:', error);
      const errorMessage = error.message || 'Autentificare eșuată';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    console.log('=== Starting logout process ===');
    try {
      if (state.refreshToken) {
        const url = `${API_BASE_URL}${API_ENDPOINTS.LOGOUT}`;
        console.log('Logout URL:', url);
        
        await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${state.token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ refreshToken: state.refreshToken })
        }).catch(console.error);
      }
    } finally {
      removeStoredData();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      console.log('=== Logout complete ===');
    }
  };

  // Permission helper
  const hasPermission = (requiredRole) => {
    if (!state.user || !state.user.role) {
      console.log('hasPermission: No user or role');
      return false;
    }
    
    const userRole = state.user.role.toLowerCase();
    const required = requiredRole.toLowerCase();
    
    console.log('hasPermission check:', { userRole, required });
    
    if (userRole === 'admin') return true;
    if (userRole === 'user' && (required === 'user' || required === 'public')) return true;
    if (required === 'public') return true;
    
    return false;
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Context value
  const value = {
    // State
    user: state.user,
    token: state.token,
    refreshToken: state.refreshToken,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    
    // Actions
    login,
    logout,
    clearError,
    
    // Helpers
    hasPermission,
    isAdmin: () => state.user?.role === 'admin',
    isUser: () => state.user?.role === 'user' || state.user?.role === 'admin',
    getUserDisplayName: () => state.user?.name || state.user?.email || 'Utilizator',
    getUserInitials: () => {
      if (!state.user?.name) return 'U';
      const names = state.user.name.split(' ');
      return names.length >= 2 
        ? `${names[0][0]}${names[1][0]}`.toUpperCase()
        : state.user.name[0].toUpperCase();
    },
    getAuthHeader: () => state.token ? { Authorization: `Bearer ${state.token}` } : {},
    isTokenExpired: () => isTokenExpired(state.token)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext };